import Actions from 'actions'
import AddToNotes from 'components/common/icons/timelineV2/scan-feed/AddToNotes'
import DownloadImages from 'components/common/icons/timelineV2/scan-feed/DownloadImages'
import SendSelectedImages from 'components/common/icons/timelineV2/scan-feed/SendSelectedImages'
import { IMAGE_MODES } from 'consts/scanSummaryConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { composePreventivePoses } from 'utils/preventiveComposer'
import { generateSelectedScanSummaryImageContents } from 'utils/scanSummaryUtils'
import { v4 } from 'uuid'
import useAddAttachmentsToNote from './useAddAttachmentsToNote'

export default ({
  imagesMode,
  analyticsPayload,
  displayedPreventiveLayers,
  selectedPoses,
  sketchedPoses = [],
  parsedScanSummaryData = {},
  isAnyPreventiveLayerVisible,
  downloadScanSummaryImages = async () => {}
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { handleAddAttachmentsToNote } = useAddAttachmentsToNote()
  const { addAttachmentsToNote: addAttachmentsToNoteFF } = useFeatureFlags()

  const [isAddingAttachmentsToNote, setIsAddingAttachmentsToNote] = useState(false)

  const handleDownloadSelected = useCallback(async () => {
    trackEvent('Scan summary - download images clicked', {
      ...analyticsPayload,
      preventiveLayers: displayedPreventiveLayers,
      poses: selectedPoses
    })
    await downloadScanSummaryImages()
  }, [analyticsPayload, selectedPoses, displayedPreventiveLayers, downloadScanSummaryImages])

  const handleSendSelectedImages = useCallback(async () => {
    // If preventive layers are visible, compose overlays client-side and send as edited images
    let editedWithPreventive = { ...sketchedPoses }
    if (displayedPreventiveLayers?.length) {
      const parsedPreventiveImages =
        parsedScanSummaryData[IMAGE_MODES.preventiveLayers] || parsedScanSummaryData[IMAGE_MODES.preventive]
      const baseImagesMode = imagesMode === IMAGE_MODES.preventive ? IMAGE_MODES.normal : imagesMode
      const composed = await composePreventivePoses({
        parsedScanSummaryData,
        parsedPreventiveImages,
        displayedPreventiveLayers,
        selectedPoses,
        baseImagesMode
      })
      editedWithPreventive = { ...editedWithPreventive, ...composed }
    }

    dispatch(
      Actions.sendSelectedScanSummaryImages({
        selectedImages: selectedPoses,
        editedImages: editedWithPreventive,
        originalImages: parsedScanSummaryData[imagesMode]
      })
    )

    trackEvent('Summary_SendClicked', {
      ...analyticsPayload,
      amount: selectedPoses.length,
      positions: selectedPoses,
      annotatedPositions: Object.keys(editedWithPreventive).filter(key => editedWithPreventive[key]),
      imagesMode
    })
  }, [
    imagesMode,
    selectedPoses,
    sketchedPoses,
    dispatch,
    parsedScanSummaryData,
    analyticsPayload,
    displayedPreventiveLayers
  ])

  const handleAddImagesToNote = useCallback(async () => {
    setIsAddingAttachmentsToNote(true)
    const scanSummaryImagesContents = await generateSelectedScanSummaryImageContents({
      parsedScanSummaryData: parsedScanSummaryData[imagesMode],
      selectedPoses,
      sketchedPoses
    })

    const attachments = scanSummaryImagesContents.map(({ content }) => ({
      type: 'image',
      contentType: 'image/png',
      data: content,
      key: `note-attachment-${v4()}.png`
    }))

    trackEvent('Scan summary actions - Attach images to note clicked', {
      numOfAttachments: attachments.length
    })

    await handleAddAttachmentsToNote({
      attachments
    })
    setIsAddingAttachmentsToNote(false)
  }, [handleAddAttachmentsToNote, imagesMode, parsedScanSummaryData, selectedPoses, sketchedPoses])

  const scanSummaryImageActions = useMemo(
    () =>
      [
        {
          icon: <SendSelectedImages />,
          label: t('pages.patients.selectedPatient.timelineV2.scanSummaryActions.sendSelected'),
          onClick: handleSendSelectedImages,
          isDisabled: !selectedPoses.length,
          condition: () => true
        },
        {
          icon: <AddToNotes />,
          label: t('pages.patients.selectedPatient.timelineV2.scanSummaryActions.addToNotes'),
          onClick: handleAddImagesToNote,
          isDisabled:
            !selectedPoses.length ||
            imagesMode === IMAGE_MODES.preventive ||
            isAnyPreventiveLayerVisible ||
            isAddingAttachmentsToNote,
          isLoading: isAddingAttachmentsToNote,
          condition: () => addAttachmentsToNoteFF
        },
        {
          icon: <DownloadImages />,
          label: t('pages.patients.selectedPatient.timelineV2.scanSummaryActions.download'),
          onClick: handleDownloadSelected,
          isDisabled: !selectedPoses.length || isAnyPreventiveLayerVisible,
          condition: () => true
        }
      ].filter(action => action.condition()),
    [
      t,
      handleSendSelectedImages,
      selectedPoses.length,
      imagesMode,
      isAnyPreventiveLayerVisible,
      handleAddImagesToNote,
      isAddingAttachmentsToNote,
      handleDownloadSelected,
      addAttachmentsToNoteFF
    ]
  )

  return scanSummaryImageActions
}
